// The realtime client: one WebSocket for the whole session.
//
// THIS IS THE FEATURE THAT JUSTIFIED THE SPA. A server-rendered page drops its
// socket on every navigation; here the connection is a module-level singleton
// that outlives every view. Nothing in this file may be tied to a view's
// lifetime.
//
// Design notes that matter:
//
//   • ONE socket, MANY channels. Subscriptions are refcounted, because two views
//     can legitimately want the same group channel at once (the feed and an
//     event card's unread badge). The last unsubscribe sends the frame.
//   • Subscriptions are RE-SENT on reconnect. The server authorizes at subscribe
//     time, so a reconnect must re-ask — and may now be refused, which is correct
//     if the user left the group in the meantime.
//   • Close code 4002 means the access token expired: refresh and reconnect
//     silently rather than bouncing the user to a login screen mid-session.
//   • Reconnect backs off with jitter. Without jitter, every client dropped by one
//     server restart comes back in the same instant.

import { getAccessToken, getRefreshToken } from './api/tokens.js';
import { refreshSession } from './api/client.js';
import { WsCloseCode, type RealtimeFrame } from '@msc/shared';
import { apiOrigin } from './apiOrigin.js';

type Handler = (frame: RealtimeFrame) => void;

let socket: WebSocket | null = null;
let reconnectTimer: number | null = null;
let attempt = 0;
let closedOnPurpose = false;

/** channel -> how many callers want it. */
const wanted = new Map<string, number>();
/** channel -> handlers for frames on it. */
const handlers = new Map<string, Set<Handler>>();
/** Channels the server has confirmed on the CURRENT socket. */
let confirmed = new Set<string>();

function wsUrl(token: string): string {
  // On native the socket must target the API origin EXPLICITLY, for exactly the
  // reason the API client does: `window.location` inside the shell is the local
  // bundle origin, which no server is listening on. Deriving from it there opens
  // a socket to the app's own scheme handler, which never upgrades. See
  // `apiOrigin.ts`.
  const origin = apiOrigin();
  if (origin) return `${origin.replace(/^http/, 'ws')}/ws?token=${encodeURIComponent(token)}`;

  // Web: same origin, so the Vite proxy handles it in dev and Apache in production.
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;
}

function connect(): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  const token = getAccessToken();
  if (!token) return; // Not signed in; nothing to connect for.

  closedOnPurpose = false;
  confirmed = new Set();

  const ws = new WebSocket(wsUrl(token));
  socket = ws;

  ws.addEventListener('open', () => {
    attempt = 0;
    // Re-subscribe to everything still wanted. The server re-authorizes each one,
    // which is the point: access can have been revoked while we were away.
    for (const channel of wanted.keys()) sendSubscribe(channel);
  });

  ws.addEventListener('message', (ev) => {
    let frame: RealtimeFrame;
    try {
      frame = JSON.parse(String(ev.data)) as RealtimeFrame;
    } catch {
      return;
    }

    if (frame.event === 'subscribed') {
      confirmed.add(frame.data.channel);
      return;
    }
    if (frame.event === 'denied') {
      // Stop asking. A denial is a real answer — usually "you left this group" —
      // and retrying it every reconnect would be a loop.
      wanted.delete(frame.data.channel);
      confirmed.delete(frame.data.channel);
      return;
    }
    if (frame.event === 'ready' || frame.event === 'unsubscribed') return;

    dispatch(frame);
  });

  ws.addEventListener('close', (ev) => {
    socket = null;
    if (closedOnPurpose) return;

    if (ev.code === WsCloseCode.TOKEN_EXPIRED) {
      // Refresh and come straight back. Not a user-visible failure.
      void refreshSession().then(() => scheduleReconnect(0));
      return;
    }
    if (ev.code === WsCloseCode.AUTH_FAILED && !getRefreshToken()) {
      return; // Genuinely signed out. Stop.
    }
    scheduleReconnect();
  });

  ws.addEventListener('error', () => {
    // 'close' always follows, so reconnection is handled there. Swallowed to keep
    // a transient network blip out of the console.
  });
}

function scheduleReconnect(delayMs?: number): void {
  if (reconnectTimer !== null) return;
  attempt++;
  // Exponential to 30s, plus jitter so a server restart does not bring every
  // client back in the same instant.
  const base = delayMs ?? Math.min(30_000, 500 * 2 ** Math.min(attempt, 6));
  const wait = base + Math.random() * 500;

  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, wait);
}

function sendSubscribe(channel: string): void {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(JSON.stringify({ action: 'subscribe', channel }));
}

function sendUnsubscribe(channel: string): void {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(JSON.stringify({ action: 'unsubscribe', channel }));
}

/**
 * Route a frame to the handlers of whichever channel it belongs to.
 *
 * The frame does not name its channel, so it is derived from the payload. That
 * keeps the wire format identical to the PHP server's and avoids echoing a
 * redundant field on every message.
 */
/**
 * Which channel a frame belongs to.
 *
 * ⚠ A frame this does not name is DROPPED — silently, with nothing thrown and
 * nothing logged. That is exactly what happened to `feed.reaction`: it was added
 * to `RealtimeFrame`, broadcast correctly by the server, and never routed here,
 * so a reaction updated only for the person who clicked it and everyone else's
 * page went on showing the old count until they reloaded. Nothing failed loudly
 * enough to notice, because the clicker's own bar rebuilds itself locally off the
 * HTTP response. Found 2026-09-09 while adding the chat equivalent.
 *
 * Hence the exhaustive `switch` with the `never` check below: every future frame
 * must be routed or explicitly declared channel-less, and forgetting is now a
 * COMPILE error rather than a feature that quietly half-works.
 *
 * Note the two arms cannot be collapsed into "does the payload have an eventId":
 * a `FeedPost` carries BOTH `groupId` and a nullable `eventId`, so sniffing the
 * shape would route every event card to an event channel nobody listens on.
 */
function channelFor(frame: RealtimeFrame): string | null {
  switch (frame.event) {
    case 'chat.message':
    case 'chat.deleted':
    case 'chat.reaction':
      return `event.${frame.data.eventId}`;
    case 'feed.post':
    case 'feed.deleted':
    case 'feed.reaction':
    // Comments ride the GROUP channel, like everything else in the feed. They
    // are gated by membership alone -- which is precisely why they exist on
    // plain posts only and never on an event card, whose conversation is
    // RSVP-gated and would be routed around by an ungated surface.
    case 'feed.comment':
    case 'feed.comment.deleted':
      return `group.${frame.data.groupId}`;
    // ⚠ `tournament.<eventId>`, NOT `event.<eventId>`, even though the payload
    // carries both an eventId and a groupId. It is its own channel family
    // because it is authorized by group membership rather than by chat access;
    // routing it to either of the arms above would deliver it to a channel the
    // tournament view never subscribes to, which is this switch's original sin.
    case 'tournament.changed':
      return `tournament.${frame.data.eventId}`;
    // Connection-level frames. Handled by the socket's own listener before
    // dispatch ever sees them; named here only to keep the switch exhaustive.
    case 'ready':
    case 'subscribed':
    case 'unsubscribed':
    case 'denied':
      return null;
    default: {
      const unrouted: never = frame;
      return unrouted;
    }
  }
}

function dispatch(frame: RealtimeFrame): void {
  const channel = channelFor(frame);
  if (!channel) return;

  for (const handler of handlers.get(channel) ?? []) {
    try {
      handler(frame);
    } catch (err) {
      // One bad handler must not stop the others, or a render error in the feed
      // would silently kill the chat.
      console.error(`realtime handler for ${channel}:`, err);
    }
  }
}

/**
 * Subscribe to a channel and receive its frames.
 *
 * Returns an unsubscribe function. CALL IT when a view unmounts — the router
 * replaces the outlet's children without telling anyone, so a view that forgets
 * leaks a handler that renders into detached nodes forever.
 */
export function subscribe(channel: string, handler: Handler): () => void {
  wanted.set(channel, (wanted.get(channel) ?? 0) + 1);

  let set = handlers.get(channel);
  if (!set) {
    set = new Set();
    handlers.set(channel, set);
  }
  set.add(handler);

  connect();
  // Only send if the socket is already open and has not confirmed this channel.
  // On a fresh connection the 'open' handler does it.
  if (!confirmed.has(channel)) sendSubscribe(channel);

  return () => {
    const remaining = (wanted.get(channel) ?? 1) - 1;
    handlers.get(channel)?.delete(handler);

    if (remaining <= 0) {
      // Refcounted: two views can want the same group channel at once, and the
      // first one closing must not cut off the second.
      wanted.delete(channel);
      handlers.delete(channel);
      confirmed.delete(channel);
      sendUnsubscribe(channel);
    } else {
      wanted.set(channel, remaining);
    }
  };
}

/** Open the socket early, so the first subscribe does not wait on a handshake. */
export function startRealtime(): void {
  if (getAccessToken() || getRefreshToken()) connect();
}

/** Close for good. Called on logout so the socket does not outlive the session. */
export function stopRealtime(): void {
  closedOnPurpose = true;
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  wanted.clear();
  handlers.clear();
  confirmed.clear();
  socket?.close(1000, 'signed out');
  socket = null;
}

/** For debugging and the smoke test. */
export function realtimeState(): { open: boolean; channels: string[] } {
  return {
    open: socket?.readyState === WebSocket.OPEN,
    channels: [...confirmed],
  };
}
